import React from "react";
import { useFetcher, useRevalidator } from "@remix-run/react";
import {
  Box,
  Button,
  InlineStack,
  Badge,
  BlockStack,
  Text,
} from "@shopify/polaris";
import { TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";
import { formatActionText } from "../utils/formatters";

export function QueueItem({ item, hideDetails = false, isReadOnly = false, readOnlyMessage = "Action simulated! Install Revivify AI to process real returns." }: { item: any, hideDetails?: boolean, isReadOnly?: boolean, readOnlyMessage?: string }) {
  const fetcher = useFetcher();
  const revalidator = useRevalidator();

  const isApproveLoading = !isReadOnly && fetcher.state !== "idle" && fetcher.formData?.get("actionType") === "APPROVE";
  const isDismissLoading = !isReadOnly && fetcher.state !== "idle" && fetcher.formData?.get("actionType") === "DISMISS";
  const isAnalyzeLoading = !isReadOnly && fetcher.state !== "idle" && fetcher.formData?.get("actionType") === "RE_ANALYZE";

  const shopify = typeof window !== "undefined" ? (window as any).shopify : null;

  React.useEffect(() => {
    const data = fetcher.data as any;
    if (data) {
      if (data.success === false && data.error) {
        if (shopify?.toast) {
          shopify.toast.show(data.error, { isError: true });
        } else {
          console.error(`[LOCAL DEV] Action failed: ${data.error}`);
        }
      } else if (data.success === true) {
        if (data.suggestedAction) {
          if (shopify?.toast) {
            const actionText = formatActionText(data.suggestedAction);
            shopify.toast.show(`AI suggested: ${actionText} (${Math.round(data.confidence * 100)}% confidence)`, { duration: 5000 });
          } else {
            console.log(`[LOCAL DEV] AI suggested: ${data.suggestedAction}`);
          }
        }
        if (revalidator.state === "idle") {
          revalidator.revalidate();
        }
      }
      console.log(`Action result for ${item.loopReturnId}:`, data);
    }
  }, [fetcher.data, item.loopReturnId, shopify, revalidator]);


  const handleAction = (actionType: string) => {
    const formData = new FormData();
    formData.append("actionType", actionType);
    formData.append("returnId", item.id);
    fetcher.submit(formData, { method: "post" });
  };

  if (hideDetails) {
    return (
      <Box paddingInlineStart="0">
        <BlockStack gap="100" align="end">
          <div data-testid={`retry-ai-button-${item.id}`} className="full-width-button retry-ai-btn">
            <Tooltip content="Forces the AI to re-analyze this return with your updated threshold settings.">
              <Button
                loading={isAnalyzeLoading}
                variant="secondary"
                size="slim"
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("RE_ANALYZE");
                }}
              >
                Retry AI
              </Button>
            </Tooltip>
          </div>

          <div data-testid={`approve-button-${item.id}`} className="full-width-button approve-btn">
            <Tooltip content="Immediately executes the AI's suggested retention action (e.g. extending the subscription).">
              <Button
                variant="primary"
                tone="success"
                size="slim"
                loading={isApproveLoading}
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("APPROVE");
                }}
              >
                Approve
              </Button>
            </Tooltip>
          </div>

          <div data-testid={`dismiss-button-${item.id}`} className="full-width-button dismiss-btn">
            <Tooltip content="Dismisses the AI's suggestion and closes the return event without taking action.">
              <Button
                variant="primary"
                tone="critical"
                size="slim"
                loading={isDismissLoading}
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("DISMISS");
                }}
              >
                Dismiss
              </Button>
            </Tooltip>
          </div>
        </BlockStack>
      </Box>
    );
  }

  return (
    <Box padding="400">
      <InlineStack align="space-between" blockAlign="center">
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text fontWeight="bold" variant="bodyLg" as="span">
              Return #{item.loopReturnId}
            </Text>
            <Badge tone="attention">Requires Action</Badge>
            {item.inference && (
              <Badge tone="info">{`${Math.round(item.inference.confidence * 100)}% AI Confidence`}</Badge>
            )}
          </InlineStack>
          <Text variant="bodyMd" tone="subdued" as="p">
            {item.inference?.logic}
          </Text>
          <Box paddingBlockStart="100">
            <Text variant="bodySm" fontWeight="bold" tone="success" as="span">
              Smart Suggestion: {formatActionText(item.inference?.suggestedAction)}
            </Text>
          </Box>
        </BlockStack>

        <BlockStack gap="200" align="end">
          <div data-testid={`retry-ai-button-${item.id}`} className="full-width-button retry-ai-btn">
            <Tooltip content="Forces the AI to re-analyze this return with your updated threshold settings.">
              <Button
                loading={isAnalyzeLoading}
                variant="secondary"
                size="medium"
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("RE_ANALYZE");
                }}
              >
                Retry AI
              </Button>
            </Tooltip>
          </div>

          <div data-testid={`approve-button-${item.id}`} className="full-width-button approve-btn">
            <Tooltip content="Immediately executes the AI's suggested retention action (e.g. extending the subscription).">
              <Button
                variant="primary"
                tone="success"
                size="medium"
                loading={isApproveLoading}
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("APPROVE");
                }}
              >
                Approve {formatActionText(item.inference?.suggestedAction)}
              </Button>
            </Tooltip>
          </div>

          <div data-testid={`dismiss-button-${item.id}`} className="full-width-button dismiss-btn">
            <Tooltip content="Dismisses the AI's suggestion and closes the return event without taking action.">
              <Button
                variant="primary"
                tone="critical"
                size="medium"
                loading={isDismissLoading}
                disabled={false}
                onClick={() => {
                  if (isReadOnly) {
                    shopify?.toast.show(readOnlyMessage, { duration: 3000 });
                    return;
                  }
                  handleAction("DISMISS");
                }}
              >
                Dismiss
              </Button>
            </Tooltip>
          </div>
        </BlockStack>
      </InlineStack>
    </Box>
  );
}
